import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'

function emptyForm() {
  return {
    patient_id: '', consultation_id: '', date_cr: new Date().toISOString().slice(0, 10),
    titre: '', anamnese: '', examen_clinique: '', diagnostic: '',
    traitement: '', conseils: '', suivi: '', destinataire_id: ''
  }
}

function initiales(prenom, nom) {
  return ((prenom?.[0] || '') + (nom?.[0] || '')).toUpperCase()
}

export default function ComptesRendus() {
  const { praticien } = useAuth()
  const [crs, setCrs] = useState([])
  const [patients, setPatients] = useState([])
  const [consultations, setConsultations] = useState([])
  const [partenaires, setPartenaires] = useState([])
  const [loading, setLoading] = useState(true)
  const [panel, setPanel] = useState(null)
  const [form, setForm] = useState(emptyForm())
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [sendingMail, setSendingMail] = useState(false)

  const load = useCallback(async () => {
    if (!praticien?.id) return
    setLoading(true)
    const { data } = await supabase.from('comptes_rendus')
      .select('*, patients(prenom, nom), praticiens_partenaires(prenom, nom, specialite)')
      .eq('praticien_id', praticien.id)
      .order('date_cr', { ascending: false })
    setCrs(data || [])
    setLoading(false)
  }, [praticien?.id])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    const handler = () => { setForm(emptyForm()); setPanel('new') }
    window.addEventListener('cabinet:new', handler)
    return () => window.removeEventListener('cabinet:new', handler)
  }, [])

  useEffect(() => {
    supabase.from('patients').select('id, prenom, nom').order('nom').then(({ data }) => setPatients(data || []))
    supabase.from('praticiens_partenaires').select('id, prenom, nom, specialite, email').order('nom').then(({ data }) => setPartenaires(data || []))
  }, [])

  async function loadConsultations(patientId) {
    const { data } = await supabase.from('consultations')
      .select('id, date_consultation, motif').eq('patient_id', patientId)
      .eq('praticien_id', praticien.id).order('date_consultation', { ascending: false }).limit(15)
    setConsultations(data || [])
  }

  async function save() {
    setSaving(true)
    const payload = { ...form, praticien_id: praticien.id, consultation_id: form.consultation_id || null, destinataire_id: form.destinataire_id || null }
    if (panel === 'new') {
      const { error } = await supabase.from('comptes_rendus').insert(payload)
      if (error) { alert('Erreur : ' + error.message); setSaving(false); return }
    } else {
      const { error } = await supabase.from('comptes_rendus').update(payload).eq('id', panel.id)
      if (error) { alert('Erreur : ' + error.message); setSaving(false); return }
    }
    setSaving(false); setPanel(null); load()
  }

  async function del() {
    if (!window.confirm('Supprimer ce compte rendu ?')) return
    await supabase.from('comptes_rendus').delete().eq('id', panel.id)
    setPanel(null); load()
  }

  function generatePDF(cr) {
    import('jspdf').then(({ jsPDF }) => {
      const doc = new jsPDF({ unit: 'mm', format: 'a4' })
      const W = 210; const margin = 20
      const pat = patients.find(p => p.id === cr.patient_id)
      const dest = partenaires.find(p => p.id === cr.destinataire_id)

      // En-tête praticienne
      doc.setFontSize(13); doc.setFont('helvetica', 'bold'); doc.setTextColor(20, 20, 20)
      doc.text('CABINET OSTEOPATHIE', margin, 25)
      doc.setFontSize(10); doc.setFont('helvetica', 'normal'); doc.setTextColor(60, 60, 60)
      doc.text(praticien.prenom + ' ' + praticien.nom, margin, 32)
      if (praticien.adresse_cabinet) { doc.text(praticien.adresse_cabinet, margin, 38) }
      if (praticien.numero_rpps) { doc.text('N RPPS : ' + praticien.numero_rpps, margin, 44) }

      // Titre + date
      doc.setFontSize(13); doc.setFont('helvetica', 'bold'); doc.setTextColor(20, 20, 20)
      doc.text('COMPTE RENDU', W - margin, 25, { align: 'right' })
      doc.setFontSize(10); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80)
      doc.text('Date : ' + format(new Date(cr.date_cr), 'dd/MM/yyyy'), W - margin, 32, { align: 'right' })

      // Séparateur
      doc.setDrawColor(180, 180, 180); doc.setLineWidth(0.3)
      doc.line(margin, 52, W - margin, 52)

      // Destinataire (si renseigné)
      let y = 60
      if (dest) {
        doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(120, 120, 120)
        doc.text('ADRESSE AU Dr ' + dest.nom.toUpperCase(), margin, y); y += 6
        doc.setFont('helvetica', 'normal'); doc.setTextColor(60, 60, 60)
        doc.text(dest.prenom + ' ' + dest.nom + (dest.specialite ? ' — ' + dest.specialite : ''), margin, y); y += 6
      }

      // Patient
      doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(120, 120, 120)
      doc.text('PATIENT :', margin, y + 4); y += 10
      doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(20, 20, 20)
      doc.text((pat?.prenom || '') + ' ' + (pat?.nom || ''), margin, y); y += 8

      // Titre CR
      if (cr.titre) {
        doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.setTextColor(20, 20, 20)
        doc.text(cr.titre, margin, y); y += 8
      }

      doc.setDrawColor(200, 200, 200); doc.line(margin, y, W - margin, y); y += 6

      // Sections
      const sections = [
        ['Anamnèse', cr.anamnese],
        ['Examen clinique', cr.examen_clinique],
        ['Diagnostic', cr.diagnostic],
        ['Traitement', cr.traitement],
        ['Conseils', cr.conseils],
        ['Suivi', cr.suivi],
      ]

      for (const [label, content] of sections) {
        if (!content) continue
        if (y > 260) { doc.addPage(); y = 20 }
        doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(80, 80, 80)
        doc.text(label.toUpperCase(), margin, y); y += 5
        doc.setFontSize(10); doc.setFont('helvetica', 'normal'); doc.setTextColor(30, 30, 30)
        const lignes = doc.splitTextToSize(content, W - margin * 2)
        lignes.forEach(l => {
          if (y > 270) { doc.addPage(); y = 20 }
          doc.text(l, margin, y); y += 5
        })
        y += 4
      }

      // Signature
      y = Math.max(y + 10, 240)
      doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(120, 120, 120)
      doc.text(praticien.prenom + ' ' + praticien.nom, W - margin, y, { align: 'right' })
      if (praticien.signature_base64) {
        try { doc.addImage(praticien.signature_base64, 'PNG', W - margin - 56, y + 3, 56, 20) } catch (e) {}
      }

      // Pied de page
      doc.setDrawColor(190, 190, 190); doc.setLineWidth(0.2); doc.line(margin, 275, W - margin, 275)
      doc.setFontSize(8); doc.setFont('helvetica', 'italic'); doc.setTextColor(140, 140, 140)
      doc.text('Document confidentiel — Usage médical uniquement', W / 2, 280, { align: 'center' })

      doc.save('CR_' + (pat?.nom || 'patient') + '_' + cr.date_cr + '.pdf')
    })
  }

  async function sendByMail(cr) {
    const dest = partenaires.find(p => p.id === cr.destinataire_id)
    if (!dest?.email) { alert("Le destinataire n'a pas d'email renseigné."); return }
    setSendingMail(cr.id)
    try {
      const pat = patients.find(p => p.id === cr.patient_id)
      const res = await fetch('https://api.resend.com/emails', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.REACT_APP_RESEND_API_KEY },
        body: JSON.stringify({
          from: 'Cabinet Osteopathie <noreply@' + (process.env.REACT_APP_EMAIL_DOMAIN || 'votre-domaine.fr') + '>',
          to: [dest.email],
          subject: 'Compte rendu — ' + (pat?.prenom || '') + ' ' + (pat?.nom || ''),
          html: '<p>Bonjour Dr ' + dest.nom + ',</p><p>Veuillez trouver ci-joint le compte rendu de ' + (pat?.prenom || '') + ' ' + (pat?.nom || '') + ' en date du ' + format(new Date(cr.date_cr), 'dd/MM/yyyy') + '.</p><p>Cordialement,<br/>' + praticien.prenom + ' ' + praticien.nom + '</p>'
        })
      })
      if (res.ok) alert('Email envoyé à ' + dest.email)
      else alert('Erreur envoi — vérifiez REACT_APP_RESEND_API_KEY dans .env')
    } catch (e) { alert('Erreur réseau') }
    setSendingMail(null)
  }

  const filtered = search
    ? crs.filter(c => `${c.patients?.prenom} ${c.patients?.nom}`.toLowerCase().includes(search.toLowerCase()) || c.titre?.toLowerCase().includes(search.toLowerCase()))
    : crs

  return (
    <>
      <div className="search-bar">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="6.5" cy="6.5" r="4.5"/><path d="M10.5 10.5l3 3"/></svg>
        <input placeholder="Rechercher par patient ou titre…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? <div className="empty">Chargement…</div> : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Date</th><th>Patient</th><th>Titre</th><th>Destinataire</th><th></th></tr></thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={5} className="empty">Aucun compte rendu</td></tr>}
              {filtered.map(cr => (
                <tr key={cr.id} className="clickable" onClick={() => { setForm({...cr, consultation_id: cr.consultation_id||'', destinataire_id: cr.destinataire_id||''}); if(cr.patient_id) loadConsultations(cr.patient_id); setPanel(cr) }}>
                  <td className="td-muted">{format(new Date(cr.date_cr), 'dd/MM/yyyy')}</td>
                  <td><div className="cell-name"><div className="avatar">{initiales(cr.patients?.prenom, cr.patients?.nom)}</div>{cr.patients?.prenom} {cr.patients?.nom}</div></td>
                  <td className="td-muted">{cr.titre || '—'}</td>
                  <td className="td-muted">{cr.praticiens_partenaires ? 'Dr ' + cr.praticiens_partenaires.nom : '—'}</td>
                  <td>
                    <div style={{display:'flex', gap:6}}>
                      <button className="btn btn-sm" onClick={e => { e.stopPropagation(); generatePDF(cr) }}>PDF</button>
                      {cr.destinataire_id && (
                        <button className="btn btn-sm" onClick={e => { e.stopPropagation(); sendByMail(cr) }} disabled={sendingMail === cr.id}>
                          {sendingMail === cr.id ? '…' : '✉'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {panel && (
        <>
          <div className="panel-overlay" onClick={() => setPanel(null)} />
          <div className="panel" style={{width:720, maxWidth:'calc(100vw - 32px)'}}>
            <div className="panel-header">
              <h3>{panel === 'new' ? 'Nouveau compte rendu' : 'Modifier le compte rendu'}</h3>
              <button className="close-btn" onClick={() => setPanel(null)}>✕</button>
            </div>
            <div className="panel-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Patient *</label>
                  <select value={form.patient_id} onChange={e => { setForm({...form, patient_id: e.target.value, consultation_id: ''}); if(e.target.value) loadConsultations(e.target.value) }}>
                    <option value="">Sélectionner…</option>
                    {patients.map(p => <option key={p.id} value={p.id}>{p.prenom} {p.nom}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" value={form.date_cr} onChange={e => setForm({...form, date_cr: e.target.value})} />
                </div>
              </div>
              {consultations.length > 0 && (
                <div className="form-group" style={{marginBottom:14}}>
                  <label>Consultation associée</label>
                  <select value={form.consultation_id} onChange={e => setForm({...form, consultation_id: e.target.value})}>
                    <option value="">— Non liée —</option>
                    {consultations.map(c => <option key={c.id} value={c.id}>{format(new Date(c.date_consultation), 'dd/MM/yyyy')} — {c.motif || 'Consultation'}</option>)}
                  </select>
                </div>
              )}
              <div className="form-row">
                <div className="form-group" style={{flex:2}}>
                  <label>Titre</label>
                  <input value={form.titre} onChange={e => setForm({...form, titre: e.target.value})} placeholder="Ex: Compte rendu de séance ostéopathique" />
                </div>
                <div className="form-group">
                  <label>Adressé à</label>
                  <select value={form.destinataire_id} onChange={e => setForm({...form, destinataire_id: e.target.value})}>
                    <option value="">— Aucun —</option>
                    {partenaires.map(p => <option key={p.id} value={p.id}>Dr {p.prenom} {p.nom} {p.specialite ? '(' + p.specialite + ')' : ''}</option>)}
                  </select>
                </div>
              </div>

              {[
                ['anamnese', 'Anamnèse', 'Motif de consultation, antécédents, histoire de la maladie…'],
                ['examen_clinique', 'Examen clinique', 'Bilan postural, tests, palpation, résultats…'],
                ['diagnostic', 'Diagnostic ostéopathique', 'Dysfonctions trouvées, zones de tension…'],
                ['traitement', 'Traitement', 'Techniques utilisées, manipulations, mobilisations…'],
                ['conseils', 'Conseils', 'Exercices, postures, recommandations…'],
                ['suivi', 'Suivi', 'Prochain RDV, orientations, précautions…'],
              ].map(([field, label, placeholder]) => (
                <div key={field} className="form-group" style={{marginBottom:14}}>
                  <label>{label}</label>
                  <textarea value={form[field]} onChange={e => setForm({...form, [field]: e.target.value})} placeholder={placeholder} style={{minHeight:80, resize:'vertical'}} />
                </div>
              ))}
            </div>
            <div className="panel-footer">
              {panel !== 'new' && <button className="btn btn-danger btn-sm" onClick={del}>Supprimer</button>}
              {panel !== 'new' && <button className="btn btn-sm" onClick={() => generatePDF(panel)}>PDF</button>}
              <div style={{flex:1}} />
              <button className="btn btn-sm" onClick={() => setPanel(null)}>Annuler</button>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !form.patient_id}>
                {saving ? 'Enregistrement…' : panel === 'new' ? 'Créer' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  )
}
